import { useEffect, useRef, useState } from "react";
import { useBrand } from "@/context/BrandContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2, Upload, ChefHat, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRESET_COLORS = ["#2e5b40", "#1e3a5f", "#7c2d12", "#4c1d95", "#0f766e", "#b45309", "#be123c", "#111827"];

export default function Settings() {
  const { brand, setBrand } = useBrand();
  const [form, setForm] = useState(brand);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setForm(brand);
  }, [brand]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 2MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/branding", { ...form, business_name: form.business_name || "FoodCost" });
      setBrand(data);
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Impostazioni</h1>
        <p className="text-muted-foreground mt-1">Personalizza il brand, i colori e i dati per le etichette</p>
      </div>

      {/* Brand */}
      <Card className="p-6 border-border space-y-6">
        <h2 className="font-display text-xl font-bold">Brand</h2>
        <div className="space-y-1.5">
          <Label>Nome del locale</Label>
          <Input
            value={form.business_name}
            onChange={(e) => set("business_name", e.target.value)}
            placeholder="Es. Trattoria da Paolo"
            data-testid="brand-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Logo</Label>
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-xl bg-secondary flex items-center justify-center overflow-hidden border border-border shrink-0">
              {form.logo ? (
                <img src={form.logo} alt="logo" className="w-full h-full object-contain" data-testid="brand-logo-preview" />
              ) : (
                <ChefHat className="w-10 h-10 text-muted-foreground" />
              )}
            </div>
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onFile} data-testid="brand-logo-input" />
              <Button variant="secondary" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="brand-upload-btn">
                <Upload className="w-4 h-4" /> Carica logo
              </Button>
              {form.logo && (
                <Button variant="ghost" onClick={() => set("logo", "")} className="gap-2 text-destructive block" data-testid="brand-remove-logo-btn">
                  <Trash2 className="w-4 h-4" /> Rimuovi
                </Button>
              )}
              <p className="text-xs text-muted-foreground">PNG, SVG o JPG · max 2MB</p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Colore principale</Label>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="color"
              value={form.primary_color || "#2e5b40"}
              onChange={(e) => set("primary_color", e.target.value)}
              className="w-12 h-10 rounded-md border border-border cursor-pointer bg-transparent"
              data-testid="brand-color-input"
            />
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => set("primary_color", c)}
                className="w-7 h-7 rounded-full border-2 border-border hover:scale-110 transition-transform"
                style={{ backgroundColor: c }}
                data-testid={`brand-color-${c.replace("#", "")}`}
              />
            ))}
            {form.primary_color && (
              <Button variant="ghost" size="sm" onClick={() => set("primary_color", "")} data-testid="brand-color-reset">
                Ripristina
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Label / producer data */}
      <Card className="p-6 border-border space-y-6">
        <h2 className="font-display text-xl font-bold">Dati per le etichette</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Nome produttore</Label>
            <Input value={form.producer_name} onChange={(e) => set("producer_name", e.target.value)} placeholder="Ragione sociale" data-testid="producer-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>P. IVA</Label>
            <Input value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} placeholder="IT01234567890" data-testid="vat-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Indirizzo stabilimento</Label>
            <Input value={form.producer_address} onChange={(e) => set("producer_address", e.target.value)} placeholder="Via, Città, CAP" data-testid="producer-address-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Contatti (telefono / email)</Label>
            <Input value={form.contacts} onChange={(e) => set("contacts", e.target.value)} placeholder="Tel · email" data-testid="contacts-input" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-4">
          <div>
            <p className="font-medium text-sm">Mostra data di produzione</p>
            <p className="text-xs text-muted-foreground">Se disattivato, in etichetta compare solo la scadenza</p>
          </div>
          <Switch
            checked={form.label_show_production_date}
            onCheckedChange={(v) => set("label_show_production_date", v)}
            data-testid="label-prod-date-switch"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Conservazione (predefinita)</Label>
            <Input value={form.label_storage_default} onChange={(e) => set("label_storage_default", e.target.value)} placeholder="Es. Conservare a +4°C" data-testid="storage-default-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Modalità d'uso (predefinita)</Label>
            <Input value={form.label_usage_default} onChange={(e) => set("label_usage_default", e.target.value)} placeholder="Es. Consumare previa cottura" data-testid="usage-default-input" />
          </div>
          <p className="text-xs text-muted-foreground sm:col-span-2">
            Questi testi compaiono in etichetta. Puoi sovrascriverli per singola ricetta.
          </p>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="brand-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva impostazioni"}
        </Button>
      </div>
    </div>
  );
}
